/** Inline alert for list searches rejected with `code: statement_timeout`.
 *
 * The backend cancelled the query because it exceeded its database statement
 * budget. The remedy is narrowing the search; grids that support it may also
 * offer a one-shot retry with the server's extended budget (which the backend
 * records in the console audit log).
 */
import { Alert, Button, type SxProps, type Theme } from '@mui/material';

interface SearchTimeoutAlertProps {
  /** The failed request already ran with the extended budget — no retry offer. */
  extendedAttempted: boolean;
  /** Offer the audited extended-budget retry. Omit on grids that don't support it. */
  onRetryExtended?: (() => void) | undefined;
  /** The retry is in flight. */
  retrying?: boolean;
  sx?: SxProps<Theme>;
}

export function SearchTimeoutAlert({
  extendedAttempted,
  onRetryExtended,
  retrying = false,
  sx,
}: SearchTimeoutAlertProps) {
  const showRetry = Boolean(onRetryExtended) && !extendedAttempted;
  return (
    <Alert
      severity="warning"
      sx={sx ?? { mb: 2 }}
      {...(showRetry
        ? {
            action: (
              <Button color="inherit" size="small" disabled={retrying} onClick={onRetryExtended}>
                {retrying ? 'Retrying…' : 'Retry with a longer timeout'}
              </Button>
            ),
          }
        : {})}
    >
      {extendedAttempted
        ? 'This search timed out even with the extended budget — narrow the time window or add more specific filters.'
        : 'This search timed out — narrow the time window or add more specific filters.'}
      {showRetry && ' If you really need this exact query, retry with a longer timeout (the retry is recorded in the audit log).'}
    </Alert>
  );
}
